/**
 * Notification Service
 * Handles push notifications with custom scheduling and quiet hours respect
 */

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.notificationQueue = [];
    this.scheduledNotifications = new Map();
    this.settings = this.loadSettings();
    this.initializeService();
  }

  /**
   * Initialize notification service and request permissions
   */
  async initializeService() {
    if ('Notification' in window) {
      this.permission = Notification.permission;
      
      if (this.permission === 'default') {
        await this.requestPermission();
      }
    }
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Load notification settings from localStorage
   */
  loadSettings() {
    const defaultSettings = {
      pushNotifications: true,
      notifyMoodChanges: true,
      notifyScheduleReminders: true,
      notifyAIMessages: true,
      notifyPartnerActivity: true,
      notifyCompatibilityAlerts: true,
      quietHoursEnabled: true,
      quietHoursStart: '22:00',
      quietHoursEnd: '08:00',
      notificationFrequency: 'immediate'
    };

    try {
      const stored = localStorage.getItem('notificationSettings');
      return stored ? { ...defaultSettings, ...JSON.parse(stored) } : defaultSettings;
    } catch (error) {
      return defaultSettings;
    }
  }

  /**
   * Update notification settings
   */
  updateSettings(newSettings) {
    this.settings = { ...this.settings, ...newSettings };
    try {
      localStorage.setItem('notificationSettings', JSON.stringify(this.settings));
    } catch (error) {
      console.error('Error saving notification settings:', error);
    }
  }

  /**
   * Check if current time is within quiet hours
   */
  isQuietHours() {
    if (!this.settings?.quietHoursEnabled) return false;

    const now = new Date();
    const currentTime = `${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`;
    
    const start = this.settings?.quietHoursStart;
    const end = this.settings?.quietHoursEnd;

    // Handle overnight quiet hours (e.g., 22:00 to 08:00)
    if (start > end) {
      return currentTime >= start || currentTime < end;
    }
    
    return currentTime >= start && currentTime < end;
  }

  /**
   * Calculate next available notification time respecting quiet hours
   */
  getNextAvailableTime() {
    if (!this.settings?.quietHoursEnabled || !this.isQuietHours()) {
      return new Date();
    }

    const now = new Date();
    const [endHour, endMinute] = this.settings?.quietHoursEnd?.split(':')?.map(Number);
    
    const nextTime = new Date(now);
    nextTime?.setHours(endHour, endMinute, 0, 0);

    // If quiet hours end time is earlier than current time, schedule for next day
    if (nextTime <= now) {
      nextTime?.setDate(nextTime?.getDate() + 1);
    }

    return nextTime;
  }

  /**
   * Send push notification with scheduling logic
   */
  async sendNotification(type, data) {
    // Check if notifications are enabled
    if (!this.settings?.pushNotifications) return;

    // Check if this notification type is enabled
    const typeKey = this.getNotificationTypeKey(type);
    if (typeKey && !this.settings?.[typeKey]) return;

    // Check permission
    if (this.permission !== 'granted') {
      const granted = await this.requestPermission();
      if (!granted) return;
    }

    // Handle notification frequency
    if (this.settings?.notificationFrequency === 'off') return;

    // Check quiet hours and schedule accordingly
    if (this.isQuietHours()) {
      this.scheduleNotification(type, data);
      return;
    }

    // Handle frequency-based batching
    if (this.settings?.notificationFrequency === 'hourly' || this.settings?.notificationFrequency === 'daily') {
      this.queueNotification(type, data);
      return;
    }

    // Send immediate notification
    this.displayNotification(type, data);
  }

  /**
   * Get notification type setting key
   */
  getNotificationTypeKey(type) {
    const typeMap = {
      'mood_compatibility': 'notifyCompatibilityAlerts',
      'partner_availability': 'notifyPartnerActivity',
      'ai_suggestion': 'notifyAIMessages',
      'mood_change': 'notifyMoodChanges',
      'schedule_reminder': 'notifyScheduleReminders'
    };
    return typeMap?.[type];
  }

  /**
   * Display browser notification
   */
  displayNotification(type, data) {
    const config = this.getNotificationConfig(type, data);
    
    try {
      const notification = new Notification(config.title, {
        body: config.body,
        icon: config.icon,
        badge: config.badge,
        tag: config.tag,
        requireInteraction: config.requireInteraction,
        data: config.data
      });

      notification.onclick = () => {
        window.focus();
        if (config?.onClick) {
          config?.onClick();
        }
        notification?.close();
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification?.close(), 10000);
    } catch (error) {
      console.error('Error displaying notification:', error);
    }
  }

  /**
   * Get notification configuration based on type
   */
  getNotificationConfig(type, data) {
    const configs = {
      mood_compatibility: {
        title: '💕 High Compatibility Match!',
        body: data?.message || "Your partner's mood aligns well with yours. This could be a great time to connect.",
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'mood-compatibility',
        requireInteraction: true,
        data: { type, url: '/mood-tracker', ...data },
        onClick: () => window.location.href = '/mood-tracker'
      },
      partner_availability: {
        title: '📅 Partner Availability Update',
        body: data?.message || "Your partner's availability has changed. Check the scheduler for new optimal times.",
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'partner-availability',
        requireInteraction: false,
        data: { type, url: '/intimacy-scheduler', ...data },
        onClick: () => window.location.href = '/intimacy-scheduler'
      },
      ai_suggestion: {
        title: '✨ AI Communication Suggestion',
        body: data?.message || 'New AI-powered insight available to enhance your connection.',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'ai-suggestion',
        requireInteraction: false,
        data: { type, url: '/ai-communication-hub', ...data },
        onClick: () => window.location.href = '/ai-communication-hub'
      },
      mood_change: {
        title: '💭 Partner Mood Update',
        body: data?.message || 'Your partner has logged a new mood entry.',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'mood-change',
        requireInteraction: false,
        data: { type, url: '/mood-tracker', ...data },
        onClick: () => window.location.href = '/mood-tracker'
      },
      schedule_reminder: {
        title: '⏰ Intimate Moment Reminder',
        body: data?.message || 'Your scheduled intimate moment is coming up soon.',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'schedule-reminder',
        requireInteraction: true,
        data: { type, url: '/intimacy-scheduler', ...data },
        onClick: () => window.location.href = '/intimacy-scheduler'
      }
    };

    return configs?.[type] || configs?.ai_suggestion;
  }

  /**
   * Queue notification for batched delivery
   */
  queueNotification(type, data) {
    this.notificationQueue?.push({ type, data, timestamp: new Date() });
    this.scheduleBatchDelivery();
  }

  /**
   * Schedule batch delivery based on frequency setting
   */
  scheduleBatchDelivery() {
    const frequency = this.settings?.notificationFrequency;
    const delay = frequency === 'hourly' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.deliverBatchedNotifications();
        this.batchTimer = null;
      }, delay);
    }
  }

  /**
   * Deliver all queued notifications as a batch
   */
  deliverBatchedNotifications() {
    if (this.notificationQueue?.length === 0) return;

    const summary = this.createBatchSummary();
    this.displayNotification('batch', summary);
    this.notificationQueue = [];
  }

  /**
   * Create summary of batched notifications
   */
  createBatchSummary() {
    const counts = {};
    this.notificationQueue?.forEach(({ type }) => {
      counts[type] = (counts?.[type] || 0) + 1;
    });

    const messages = [];
    if (counts?.mood_compatibility) messages?.push(`${counts?.mood_compatibility} compatibility matches`);
    if (counts?.partner_availability) messages?.push(`${counts?.partner_availability} availability updates`);
    if (counts?.ai_suggestion) messages?.push(`${counts?.ai_suggestion} AI suggestions`);

    return {
      message: `You have ${messages?.join(', ')} waiting for you.`,
      count: this.notificationQueue?.length
    };
  }

  /**
   * Schedule notification for later delivery
   */
  scheduleNotification(type, data) {
    const deliveryTime = this.getNextAvailableTime();
    const notificationId = `${type}-${Date.now()}`;

    const timeoutId = setTimeout(() => {
      this.displayNotification(type, data);
      this.scheduledNotifications?.delete(notificationId);
    }, deliveryTime?.getTime() - Date.now());

    this.scheduledNotifications?.set(notificationId, {
      type,
      data,
      deliveryTime,
      timeoutId
    });
  }

  /**
   * Cancel a scheduled notification
   */
  cancelScheduledNotification(notificationId) {
    const scheduled = this.scheduledNotifications?.get(notificationId);
    if (scheduled) {
      clearTimeout(scheduled?.timeoutId);
      this.scheduledNotifications?.delete(notificationId);
    }
  }

  /**
   * Clear all scheduled notifications
   */
  clearAllScheduled() {
    this.scheduledNotifications?.forEach(({ timeoutId }) => clearTimeout(timeoutId));
    this.scheduledNotifications?.clear();
  }

  /**
   * Trigger mood compatibility notification
   */
  notifyMoodCompatibility(compatibilityScore, partnerMood, userMood) {
    if (compatibilityScore >= 80) {
      this.sendNotification('mood_compatibility', {
        message: `High compatibility match (${compatibilityScore}%)! Your ${userMood} mood aligns well with your partner's ${partnerMood} mood.`,
        score: compatibilityScore,
        partnerMood,
        userMood
      });
    }
  }

  /**
   * Trigger partner availability notification
   */
  notifyPartnerAvailability(availabilityChange, date, availabilityScore) {
    this.sendNotification('partner_availability', {
      message: `Your partner's availability for ${date} has ${availabilityChange}. New optimal times available.`,
      date,
      availabilityScore,
      changeType: availabilityChange
    });
  }

  /**
   * Trigger AI communication suggestion notification
   */
  notifyAISuggestion(suggestionType, priority, preview) {
    const priorityEmoji = priority === 'high' ? '🔥' : priority === 'medium' ? '⭐' : '💡';
    this.sendNotification('ai_suggestion', {
      message: `${priorityEmoji} ${preview}`,
      suggestionType,
      priority
    });
  }

  /**
   * Send test notification
   */
  sendTestNotification() {
    this.sendNotification('schedule_reminder', {
      message: 'This is a test notification from LoveNudge. Your notifications are working correctly!'
    });
  }

  /**
   * Send intelligent mood change notification with Claude-generated personalized message
   */
  async notifyIntelligentMoodChange(moodData, partnerHistory, relationshipContext) {
    try {
      // Dynamically import to avoid circular dependencies
      const { generateMoodChangeNotification } = await import('./anthropicService.js');
      
      const intelligentNotification = await generateMoodChangeNotification(
        moodData,
        partnerHistory,
        relationshipContext
      );

      // Send notification with Claude-generated content
      await this.sendNotification('mood_change', {
        message: intelligentNotification?.message,
        timingSuggestion: intelligentNotification?.timingSuggestion,
        priority: intelligentNotification?.priority,
        suggestedAction: intelligentNotification?.suggestedAction,
        aiGenerated: true,
      });

      return intelligentNotification;
    } catch (error) {
      // Silently handle internal API errors (insufficient credits, auth issues, etc.)
      if (error?.isInternal || error?.name === 'SilentError' || error?.statusCode === 402 || error?.message?.includes('credit') || error?.message?.includes('Insufficient credits')) {
        console.warn('AI features temporarily unavailable due to API credits. Using standard notifications.');
      } else {
        console.error('Error generating intelligent mood notification:', error?.message || error);
      }
      
      // Fallback to standard notification
      await this.sendNotification('mood_change', {
        message: `Your partner's mood has changed to ${moodData?.mood}. This could be a good time to connect.`,
      });
    }
  }

  /**
   * Send intelligent availability change notification with Claude-generated optimal timing
   */
  async notifyIntelligentAvailability(availabilityData, scheduleHistory, relationshipContext) {
    try {
      // Dynamically import to avoid circular dependencies
      const { generateAvailabilityNotification } = await import('./anthropicService.js');
      
      const intelligentNotification = await generateAvailabilityNotification(
        availabilityData,
        scheduleHistory,
        relationshipContext
      );

      // Send notification with Claude-generated content
      await this.sendNotification('partner_availability', {
        message: intelligentNotification?.message,
        optimalTimeSlots: intelligentNotification?.optimalTimeSlots,
        priority: intelligentNotification?.priority,
        compatibilityReason: intelligentNotification?.compatibilityReason,
        aiGenerated: true,
      });

      return intelligentNotification;
    } catch (error) {
      // Silently handle internal API errors (insufficient credits, auth issues, etc.)
      if (error?.isInternal || error?.name === 'SilentError' || error?.statusCode === 402 || error?.message?.includes('credit') || error?.message?.includes('Insufficient credits')) {
        console.warn('AI features temporarily unavailable due to API credits. Using standard notifications.');
      } else {
        console.error('Error generating intelligent availability notification:', error?.message || error);
      }

      // Fallback to standard notification
      await this.sendNotification('partner_availability', {
        message: "Your partner's availability has changed. Check the scheduler for new opportunities to connect.",
      });
    }
  }

  /**
   * Get notification permission status
   */
  getPermissionStatus() {
    return this.permission;
  }

  /**
   * Get scheduled notifications count
   */
  getScheduledCount() {
    return this.scheduledNotifications?.size;
  }

  /**
   * Get queued notifications count
   */
  getQueuedCount() {
    return this.notificationQueue?.length;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
